import { BlockSpacingConfig, SpacingPreset } from './types';

/**
 * Block-specific spacing configurations
 *
 * Spacing values translate to the following pixels:
 * Mobile (<md):
 * - full: 80px
 * - half: 40px
 * - disabled: 0px
 *
 * Desktop (≥md):
 * - full: 140px
 * - half: 70px
 * - disabled: 0px
 *
 * Custom values can be specified using the format:
 * { mobile: number, desktop: number }
 */
export const blockSpacingConfig: Record<string, BlockSpacingConfig> = {
  DatoCmsHero: {
    default: {
      top: 'disabled',
      bottom: 'disabled',
    },
  },
  DatoCmsKeyFiguresV2: {
    default: {
      top: 'disabled',
      bottom: 'half',
    },
  },
  DatoCmsContent: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsPricingPageV3: {
          top: 'disabled',
          bottom: 'disabled',
        },
      },
    },
  },
  DatoCmsHubspotForm: {
    default: {
      top: 'half',
      bottom: 'half',
    },
  },
  DatoCmsImageBlock: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      firstBlock: {
        top: 'disabled',
        bottom: 'disabled',
      },
      adjacentSame: {
        top: 'half',
        bottom: 'half',
      },
      specific: {
        DatoCmsWhyPayfitPage: {
          top: 'disabled',
          bottom: 'full',
        },
        DatoCmsGenericPage: {
          top: 'disabled',
          bottom: 'disabled',
        },
      },
    },
  },
  DatoCmsContentWithItemsList: {
    default: {
      top: 'full',
      bottom: 'half',
    },
  },
  DatoCmsEmbedArcade: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      firstBlock: {
        top: 'disabled',
        bottom: 'full',
      },
      specific: {
        DatoCmsWhyPayfitPage: {
          top: 'disabled',
          bottom: 'full',
        },
        DatoCmsGenericPage: {
          top: 'disabled',
          bottom: 'disabled',
        },
      },
    },
  },
  DatoCmsTablePriceBlock: {
    default: {
      top: 'disabled',
      bottom: 'half',
    },
  },
  DatoCmsTextSection: {
    default: {
      top: 'disabled',
      bottom: 'full',
    },
  },
  DatoCmsVideoModule: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      firstBlock: {
        top: 'disabled',
        bottom: 'full',
      },
    },
  },
  DatoCmsComparisonTable: {
    default: {
      top: 'half',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'full',
        },
      },
    },
  },
  DatoCmsLogoListStatic: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsThankYouPage: {
          top: 'half',
          bottom: 'full',
        },
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsValuePropositionMinimal: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsClientsByPage: {
          top: 'half',
          bottom: 'half',
        },
        DatoCmsServicePage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsTabsValueProposition: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsBenefitsSection: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsVersusSection: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsTestimonialCarousselAutoScrolling: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsFaq: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
        DatoCmsClientsByPage: {
          top: 'half',
          bottom: 'half',
        },
        DatoCmsPillarPage: {
          top: 'full',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsHowItWork: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'full',
        },
      },
    },
  },
  DatoCmsSimpleCardsCarousel: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsPillarPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsCardsGrid: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsPillarPage: {
          top: 'half',
          bottom: 'full',
        },
        DatoCmsServicePage: {
          top: 'half',
          bottom: 'full',
        },
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsTabsV2: {
    default: {
      top: 'half',
      bottom: 'half',
    },
  },
  DatoCmsConversionBanner: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'half',
          bottom: 'full',
        },
        DatoCmsClientsByPage: {
          top: 'half',
          bottom: 'full',
        },
        DatoCmsServicePage: {
          top: 'half',
          bottom: 'full',
        },
      },
    },
  },
  DatoCmsFeaturesList: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsGenericPage: {
          top: 'full',
          bottom: 'half',
        },
        DatoCmsClientsByPage: {
          top: 'half',
          bottom: 'full',
        },
      },
    },
  },
  DatoCmsIntegration: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsClientsByPage: {
          top: 'full',
          bottom: 'full',
        },
      },
    },
  },
  DatoCmsTestimonialStatic: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsClientsByPage: {
          top: 'half',
          bottom: 'half',
        },
      },
    },
  },
  DatoCmsTestimonialSlider: {
    default: {
      top: 'full',
      bottom: 'full',
    },
    contextual: {
      specific: {
        DatoCmsPillarPage: {
          top: 'full',
          bottom: 'half',
        },
        DatoCmsServicePage: {
          top: 'full',
          bottom: 'half',
        },
        DatoCmsGenericPage: {
          top: 'disabled',
          bottom: 'half',
        },
      },
    },
  },
};

/**
 * Default spacing configuration for blocks with the same background color
 * Reduces spacing between blocks to maintain visual hierarchy
 */
export const adjacentSameBackgroundSpacing: SpacingPreset = {
  top: 'half',
  bottom: 'half',
};

/**
 * Default spacing applied when no specific configuration is found
 * Ensures consistent spacing across the application
 */
export const defaultSpacing: SpacingPreset = {
  top: 'full',
  bottom: 'full',
};
